/**
 * Activation funnel — the ONE source of truth for what an "activated" user is.
 *
 * Every surface (marketing free tool, Chrome extension, dashboard, server)
 * imports these names and property helpers so the six steps can never drift on
 * what an "activated" user means. See docs/activation-funnel.md for the full
 * spec (when each event fires, its properties, and the PostHog funnel insight).
 *
 * The ordered funnel:
 *   free_scan_started
 *     -> scan_completed
 *     -> first_issue_created   (the "aha": an issue carrying CSS + a screenshot)
 *     -> account_created
 *     -> export_used
 *     -> paid_checkout_completed
 *
 * These are STABLE analytics contract strings. Renaming one breaks the PostHog
 * funnel insight and every historical comparison — treat the values as frozen.
 */
export const FUNNEL_EVENTS = {
    freeScanStarted: 'free_scan_started',
    scanCompleted: 'scan_completed',
    firstIssueCreated: 'first_issue_created',
    accountCreated: 'account_created',
    exportUsed: 'export_used',
    paidCheckoutCompleted: 'paid_checkout_completed',
};
/**
 * The funnel in order. Drives the PostHog funnel insight (one step per entry)
 * and any per-step conversion math. Order is load-bearing — it defines the
 * sequence PostHog counts drop-off against.
 */
export const ACTIVATION_FUNNEL_ORDER = [
    FUNNEL_EVENTS.freeScanStarted,
    FUNNEL_EVENTS.scanCompleted,
    FUNNEL_EVENTS.firstIssueCreated,
    FUNNEL_EVENTS.accountCreated,
    FUNNEL_EVENTS.exportUsed,
    FUNNEL_EVENTS.paidCheckoutCompleted,
];
/** Surfaces that can emit funnel events. `surface` is stamped on every event. */
export const FUNNEL_SURFACES = ['marketing', 'extension', 'dashboard', 'server'];
/**
 * Property keys stamped on EVERY funnel event, regardless of surface, so the six
 * steps join cleanly on one person and one project in PostHog:
 *   - surface:      which surface emitted it (required)
 *   - is_anonymous: true before the user has an account (free scan / first issue)
 *   - project_id:   the OverlayQA project, where one exists at emit time
 *   - session_id:   the anonymous/session id, where the surface has one
 */
export const FUNNEL_STANDARD_PROPERTIES = [
    'surface',
    'is_anonymous',
    'project_id',
    'session_id',
];
export const FUNNEL_EVENT_SPECS = [
    {
        name: FUNNEL_EVENTS.freeScanStarted,
        step: 1,
        title: 'Free scan started',
        when: 'A visitor kicks off a scan (server free checker, or an extension a11y scan). Fires for anonymous and authenticated users.',
        properties: ['scan_type', 'target_host'],
        emittedBy: ['marketing', 'extension', 'server'],
    },
    {
        name: FUNNEL_EVENTS.scanCompleted,
        step: 2,
        title: 'Scan completed',
        when: 'A started scan finished and results are available. Fired once per scan at the universal completion point (not the authed-only AI pass).',
        properties: ['scan_type', 'violation_count', 'score', 'duration_ms'],
        emittedBy: ['marketing', 'extension', 'server'],
    },
    {
        name: FUNNEL_EVENTS.firstIssueCreated,
        step: 3,
        title: 'First issue created',
        when: 'The "aha": the identity\'s FIRST created issue that carries CSS and/or a screenshot. Fires at most once per install/identity. Also fires server-side on the no-account web annotate session a free tool handed off to (the guest\'s first pin), stamped surface=server + is_anonymous.',
        properties: ['has_css', 'has_screenshot', 'pin_type', 'severity', 'type'],
        emittedBy: ['extension', 'server'],
    },
    {
        name: FUNNEL_EVENTS.accountCreated,
        step: 4,
        title: 'Account created',
        when: 'A brand-new OverlayQA account row is created at first authenticated bootstrap (the new-team branch, not every login).',
        properties: ['plan', 'is_trial', 'trial_expires_at'],
        emittedBy: ['server'],
    },
    {
        name: FUNNEL_EVENTS.exportUsed,
        step: 5,
        title: 'Export used',
        when: 'At least one issue was successfully exported to Jira, Linear, Notion, Asana, or Trello.',
        properties: ['provider', 'issue_count', 'success_count'],
        emittedBy: ['extension', 'dashboard'],
    },
    {
        name: FUNNEL_EVENTS.paidCheckoutCompleted,
        step: 6,
        title: 'Paid checkout completed',
        when: 'A paid subscription was created/activated on a recognized paid plan (first revenue). Trial grants do NOT fire this.',
        properties: ['plan_slug', 'plan_name', 'status', 'clerk_event'],
        emittedBy: ['server'],
    },
];
const SPEC_BY_NAME = FUNNEL_EVENT_SPECS.reduce((acc, spec) => {
    acc[spec.name] = spec;
    return acc;
}, {});
/** Type guard: is this string one of the six canonical funnel events? */
export function isFunnelEvent(name) {
    return name in SPEC_BY_NAME;
}
/** Look up the spec for a funnel event. */
export function getFunnelEventSpec(name) {
    return SPEC_BY_NAME[name];
}
/**
 * All property keys allowed on a given funnel event: the standard envelope keys
 * plus the event's step-specific keys. The extension tracking allowlist uses
 * this so its per-event property whitelist can never drift from the spec.
 */
export function funnelEventAllowedProperties(name) {
    return [...FUNNEL_STANDARD_PROPERTIES, ...SPEC_BY_NAME[name].properties];
}
/**
 * Build the standard property envelope for a funnel event. Every surface calls
 * this so the property shape can't drift. `surface` is always stamped; any
 * `undefined` extras are dropped so we never send `project_id: undefined` (which
 * would create a spurious property key in PostHog).
 *
 * Note: PostHog client wrappers already stamp `$lib`/`$environment`, and the
 * distinct_id (person identity) is handled by each wrapper — this helper only
 * shapes the event PROPERTIES.
 */
export function funnelEventProperties(surface, extra) {
    const props = { surface };
    if (extra) {
        for (const [key, value] of Object.entries(extra)) {
            if (value !== undefined)
                props[key] = value;
        }
    }
    return props;
}
//# sourceMappingURL=funnelEvents.js.map